// pt-BR locale (RF-LANG-04). Complete since D36: apps/web/test/i18n-guard.test.ts fails when an English key has no
// pt-BR entry (the runtime still falls back to English). It never replaces the English source strings.
import type { MessageKey } from "./en.ts";
import { ptBRWave2 } from "./pt-BR-wave2.ts";
import { ptBRWave3 } from "./pt-BR-wave3.ts";
import { ptBRModules } from "./pt-BR-modules.ts";
import { ptBRAgents } from "./pt-BR-agents.ts";
import { ptBRWave5 } from "./pt-BR-wave5.ts";
import { ptBRRouting } from "./pt-BR-routing.ts";
import { ptBRAssistant } from "./pt-BR-assistant.ts";
import { ptBROnboarding } from "./pt-BR-onboarding.ts";
import { ptBRMicrosystem } from "./pt-BR-microsystem.ts";
import { ptBRGuardian } from "./pt-BR-guardian.ts";
import { ptBRBlocks } from "./pt-BR-blocks.ts";
import { ptBRSecurity } from "./pt-BR-security.ts";
import { ptBRComposer } from "./pt-BR-composer.ts";
import { ptBRCore } from "./pt-BR-core.ts";
import { ptBRShell } from "./pt-BR-shell.ts";
import { ptBRContent } from "./pt-BR-content.ts";
import { ptBRDependencies } from "./pt-BR-dependencies.ts";
import { ptBROperations } from "./pt-BR-operations.ts";
import { ptBRGrant } from "./pt-BR-grant.ts";
import { ptBRSkills } from "./pt-BR-skills.ts";

export const ptBR: Partial<Record<MessageKey, string>> = {
  "project.files.exclusiveRequired": "A leitura e a criação de arquivos estão disponíveis. Editar e excluir arquivos existentes exige um workspace isolado com acesso exclusivo de escrita.",
  // Core keys translated by the D36 audit come first, so the explicit entries below still win.
  ...ptBRCore,
  // Project overview and task detail
  // Multi-factor enrollment (D30)
  "auth.mfaCode": "Código de autenticação",
  "auth.mfaCodeHint": "Digite o código de 6 dígitos do seu aplicativo autenticador.",
  "auth.mfaCodeInvalid": "Digite o código de 6 dígitos do seu aplicativo autenticador.",
  "mfa.signInAgain": "Entrar de novo com o seu código",
  "mfa.enrollTitle": "Proteja sua conta",
  "mfa.enrollSubtitle": "Administradores e mantenedores precisam usar autenticação em dois fatores.",
  "mfa.step1": "Abra um aplicativo autenticador (TOTP) ou o seu gerenciador de senhas.",
  "mfa.step2": "Adicione a conta escaneando o QR code ou digitando a chave de configuração:",
  "mfa.step3": "Digite o código que o aplicativo mostrar para confirmar.",
  "mfa.secretLabel": "Chave de configuração",
  "mfa.openAuthenticator": "Abrir no aplicativo autenticador",
  "mfa.qrLabel": "Escaneie este QR code com o seu autenticador ou gerenciador de senhas (1Password, Google Authenticator, Authy…):",
  "mfa.qrAlt": "QR code com a chave de configuração do autenticador",
  "mfa.confirm": "Confirmar e continuar",
  "mfa.verifyTitle": "Confirme que é você",
  "mfa.verifySubtitle": "Sua sessão ainda não foi verificada. Entre de novo e digite o código do seu aplicativo autenticador.",
  "mfa.verify": "Verificar",
  "overview.chatInput": "Orientação para o projeto",
  "overview.chatPlaceholder": "Enviar orientação…",
  "overview.attach": "Anexar arquivo",
  "overview.assistantUnavailable": "Assistente não configurado — chega numa fase seguinte",
  "overview.assistantUnavailableBody": "O assistente do projeto precisa de uma rota de modelo configurada. Até lá, crie tarefas pelo Kanban ou importe um PRD.",
  "overview.stepsSummary": "{active} agentes trabalhando · {waiting} aguardando recurso · {failed} com falha · {total} tarefas fora do backlog",
  "overview.expandSteps": "Expandir trabalho em andamento",
  "overview.collapseSteps": "Recolher trabalho em andamento",
  "overview.noStepsTitle": "Nada em andamento ainda",
  "overview.noStepsBody": "As tarefas aparecem aqui quando saem do backlog. Importe um PRD ou crie tarefas para planejar o trabalho.",
  "overview.importPrd": "Importar PRD",
  "overview.openKanban": "Abrir Kanban",
  "overview.openInKanban": "Abrir no Kanban",
  "overview.timelineFailed": "Não foi possível carregar a linha do tempo",
  "overview.timelineEmpty": "Nenhum evento registrado ainda.",
  "overview.timelineMore": "Mostrando os primeiros {n} eventos; há eventos mais recentes.",
  "overview.usageUnknown": "Tokens e custo: ainda não medidos",
  "overview.panels": "Mostrar painel",
  "overview.resizeSplit": "Redimensionar prévia e chat",
  "task.title": "Título",
  "task.descriptionField": "Descrição",
  "task.evidence": "Evidências",
  "task.type": "Tipo",
  "task.risk": "Risco",
  "task.origin": "Origem",
  "task.prd": "PRD",
  "task.priority": "Prioridade",
  "task.priorityShort": "Prioridade {score}",
  "task.dependencies": "Dependências",
  "task.dependsOn": "Depende de {list}",
  "task.requirements": "Requisitos",
  "task.dueAt": "Prazo",
  "task.agentModel": "Agente / modelo",
  "task.notReportedYet": "Ainda não informado",
  "task.updated": "Atualizada",
  "task.none": "Nenhuma",
  "task.fromPrd": "Do PRD",
  "task.returnsTo": "Volta para {column}",
  "task.acceptance": "Checklist de aceite",
  "task.noAcceptance": "Nenhum critério de aceite registrado.",
  "task.satisfied": "atendido",
  "task.notSatisfied": "não atendido",
  "task.timeline": "Linha do tempo",
  "task.time": "Tempo",
  "task.priorityReason": "Prioridade: {reason}",
  "precision.measured": "medido",
  "precision.estimated": "estimado",
  "precision.incomplete": "incompleto",
  "precision.unknown": "precisão desconhecida",
  "kanban.loadMore": "Carregar +{count}/{remaining}",
  "kanban.card.workingFor": "Tempo da tentativa: {duration}",
  "kanban.card.waitingFor": "Tempo da tentativa: {duration} · Aguardando recurso",
  "kanban.card.inColumnFor": "Em {column} há {duration}",
  "kanban.card.finishedAgo": "Finalizada há {duration}",
  "kanban.card.noActiveAttempt": "Nenhuma tentativa de agente ativa",
  "kanban.card.executionUnknown": "Execução do agente não informada",
  "kanban.startPending": "Início aceito · Aguardando tentativa de agente",
  "kanban.startAccepted": "Pedido de início aceito para a tarefa #{number}. Aguardando tentativa de agente.",
  "kanban.card.agent": "Agente",
  "kanban.card.model": "Modelo",
  "kanban.card.viaRoute": "via {model}",
  "kanban.card.modelPending": "modelo efetivo ainda não informado",
  "kanban.card.attemptOf": "Tentativa {number} · {role}",
  "kanban.card.score": "score {score}",
  "kanban.card.scoreDetails": "Score de prioridade {score}: ver o detalhamento",
  "createTask.title": "Nova tarefa",
  "createTask.fixTitle": "Corrigir um problema",
  "createTask.description": "Descreva a demanda. O texto original é mantido junto de qualquer enriquecimento.",
  "createTask.project": "Projeto",
  "createTask.projectsFailed": "Não foi possível carregar seus projetos.",
  "createTask.noProjects": "Crie um projeto primeiro — toda tarefa pertence a um projeto.",
  "createTask.originalKept": "Mantido literalmente como texto original.",
  "createTask.evidenceHint": "Mensagens de erro, links ou passos para reproduzir.",
  "createTask.submit": "Criar tarefa",
  "createTask.failed": "A tarefa não foi criada",
  "createTask.created": "Tarefa #{number} criada.",
  "createTask.placement": "Posição na fila",
  "createTask.placement.backlog": "Deixar no Backlog (padrão)",
  "createTask.placement.backlogHint": "Fica no Backlog com a prioridade calculada.",
  "createTask.placement.prioritized": "Priorizar — próxima a iniciar",
  "createTask.placement.prioritizedHint": "Vai para o topo da fila e para A fazer assim que as dependências permitirem.",
  "createTask.placement.start_now": "Iniciar imediatamente",
  "createTask.placement.start_nowHint": "Priorizada e iniciada agora se o projeto estiver em execução, pronto e com vaga.",
  "createTask.placement.roleHint": "Priorizar e iniciar exigem o papel de Mantenedor no projeto.",
  "createTask.createdPrioritized": "Tarefa #{number} criada e priorizada — próxima a iniciar.",
  "createTask.createdStarted": "Tarefa #{number} criada e iniciada.",
  "createTask.resultTitlePrioritized": "Tarefa #{number} criada e priorizada",
  "createTask.resultTitleNotStarted": "Tarefa #{number} criada e priorizada, mas não iniciada",
  "createTask.resultStatus": "Coluna atual: {status}.",
  "createTask.resultDetail": "Detalhe do servidor",
  "placementReason.dependencies_pending": "Ela fica no Backlog, no topo, até as dependências serem concluídas.",
  "placementReason.not_promotable": "Ela fica no Backlog, no topo: ainda não pode ir para A fazer.",
  "placementReason.priority_tied": "Ela está empatada na prioridade máxima; empates iniciam por ordem de chegada.",
  "placementReason.not_ready": "O projeto não está pronto para executar. Confira os itens de prontidão abaixo.",
  "placementReason.engine_unavailable": "O motor de execução está indisponível neste ambiente.",
  "placementReason.project_not_active": "A execução do projeto não está ativa. Inicie o projeto para executá-la primeiro.",
  "placementReason.at_capacity": "O limite de tarefas simultâneas foi atingido; ela inicia primeiro quando houver vaga.",
  "placementReason.engine_error": "O motor recusou o início; a tarefa continua no topo de A fazer.",
  "setup.setupToken": "Token de configuração",
  "setup.setupTokenHint": "Obrigatório em servidores: o token de uso único configurado pelo operador (SETUP_TOKEN).",
  "common.all": "Todos",
  "common.back": "Voltar",
  "common.cancel": "Cancelar",
  "common.close": "Fechar",
  "common.discard": "Descartar alterações",
  "common.dismiss": "Dispensar",
  "common.done": "Concluir",
  "common.laterPhase": "chega em uma fase posterior",
  "common.loading": "Carregando…",
  "common.notAvailableYet": "Ainda não disponível — chega em uma fase posterior",
  "common.notAvailableYetBody": "Este recurso não faz parte da versão atual. Nada aqui é simulado.",
  "common.optional": "opcional",
  "common.refresh": "Atualizar",
  "common.remove": "Remover",
  "common.retry": "Tentar novamente",
  "common.retrySafe": "Tentar de novo é seguro: a mesma chave de requisição é reutilizada e nada é duplicado.",
  "common.save": "Salvar",
  "common.skipToContent": "Pular para o conteúdo",
  "error.generic": "Algo deu errado. Tente novamente.",
  "error.loadFailed": "Não foi possível carregar estes dados",
  "error.correlation": "Referência: {id}",
  "validation.required": "Campo obrigatório.",

  "nav.home": "Início",
  "nav.projects": "Projetos",
  "nav.runs": "Execuções",
  "nav.prds": "PRDs",
  "nav.memory": "Memória",
  "nav.settings": "Configurações",
  "nav.inbox": "Caixa de entrada",
  "nav.agents": "Agentes e modelos",
  "nav.skills": "Skills",
  "nav.integrations": "Integrações",
  "nav.observability": "Observabilidade e auditoria",
  "nav.recent": "Recentes",
  "nav.recentEmpty": "Nenhum projeto ainda.",
  "nav.openMenu": "Abrir navegação",
  "nav.closeMenu": "Fechar navegação",
  "nav.workspace": "Workspace",
  "user.language": "Idioma",
  "user.signOut": "Sair",

  "conn.health.checking": "Verificando API…",
  "conn.health.unreachable": "API inacessível",
  "conn.health.healthy": "API saudável",
  "conn.health.degraded": "API degradada",
  "conn.health.unavailable": "API indisponível",
  "conn.health.unknown": "Estado da API desconhecido",
  "conn.checkedAt": "verificado às {time}",
  "conn.live": "Atualizações ao vivo",
  "conn.connecting": "Conectando às atualizações…",
  "conn.reconnecting": "Reconectando…",
  "conn.offline": "Offline",
  "conn.updatedAt": "atualizado às {time}",
  "conn.staleSince": "os dados podem estar desatualizados desde {time}",
  "conn.retryNow": "Tentar agora",
  "conn.staleNotice": "As atualizações ao vivo foram interrompidas. Os dados podem estar desatualizados desde {time}.",

  "home.title": "O que vamos construir hoje?",
  "home.subtitle": "Descreva sua ideia. Seus agentes cuidam das próximas etapas.",
  "home.attachPrd": "Anexar PRD",
  "home.newProject": "Novo projeto",
  "home.send": "Enviar",
  "home.createApp": "Criar uma aplicação",
  "home.importRepo": "Importar repositório",
  "home.fixProblem": "Corrigir um problema",
  "home.continue": "Continue de onde parou",
  "home.viewAll": "Ver todos os projetos",
  "home.emptyTitle": "Nenhum projeto ainda",
  "home.configureInfra": "Configurar infraestrutura",

  "projects.title": "Seus projetos",
  "projects.subtitle": "Da primeira ideia à aplicação funcionando.",
  "projects.new": "Novo projeto",
  "projects.searchPlaceholder": "Buscar projeto…",
  "projects.filter.all": "Todos",
  "projects.filter.running": "Em execução",
  "projects.filter.needs_attention": "Precisam de atenção",
  "projects.filter.completed": "Concluídos",
  "projects.noPreview": "Nenhuma prévia capturada ainda",
  "projects.progress": "{done} de {total} tarefas obrigatórias aceitas",
  "projects.action.follow": "Acompanhar",
  "projects.action.resolve": "Resolver acesso",
  "projects.action.open": "Abrir",

  "aggregate.not_started": "Não iniciado",
  "aggregate.running": "Em execução",
  "aggregate.needs_attention": "Precisa de atenção",
  "aggregate.blocked": "Bloqueado",
  "aggregate.completed": "Concluído",
  "aggregate.paused": "Pausado",

  "project.pause": "Pausar",
  "project.resume": "Retomar",
  "project.openPreview": "Abrir prévia",
  "project.tab.overview": "Visão geral",
  "project.tab.kanban": "Kanban",
  "project.files.create": "Criar arquivo",
  "project.files.delete": "Excluir arquivo",
  "project.files.check": "Verificar operação",
  "project.files.unknown": "O resultado é desconhecido. Verifique a operação antes de fazer outra alteração.",
  "project.files.pending": "Aguardando o runner autorizado.",
  "project.files.error": "A operação não foi concluída. Verifique o runner, as permissões e se o arquivo mudou.",
  "project.files.truncated": "A listagem atingiu o limite de segurança. Alguns arquivos não são exibidos.",
  "project.files.list": "Lista de arquivos do projeto",
  "project.files.empty": "Nenhum arquivo encontrado.",
  "project.files.path": "Caminho relativo (.txt, .md ou .env)",
  "project.files.content": "Conteúdo do arquivo",
  "project.files.envWarning": "O conteúdo ENV aparece apenas na sua sessão admin. Feche o editor ao terminar.",
  "project.files.agents": "Solicitar alteração de código por uma tarefa de agente",
  "project.files.undo": "Desfazer última alteração",
  "project.files.deleteConfirm": "Excluir este arquivo? Um backup cifrado será mantido e você poderá desfazer a exclusão.",
  "project.tab.files": "Arquivos",
  "project.files.title": "Arquivos do projeto — indisponíveis",
  "project.files.body": "A navegação de arquivos aguarda a conexão com o runner autorizado. Criar, atualizar e excluir arquivos ainda não está disponível.",
  "project.files.policy": "Edite TEXT e Markdown aqui. Alterações de código passam pelos agentes. ENV é restrito a sessões humanas admin.",
  "project.tab.code": "Código",
  "project.tab.runs": "Execuções",
  "project.tab.memory": "Memória",
  "project.tab.settings": "Configurações",
  "overview.preview": "Prévia da aplicação",
  "overview.chat": "Converse com o projeto",
  "overview.steps": "Trabalho em andamento",

  "status.backlog": "Backlog",
  "status.in_analysis": "Em análise",
  "status.todo": "A fazer",
  "status.in_progress": "Em andamento",
  "status.testing": "Testando",
  "status.in_review": "Em revisão",
  "status.integrating": "Integrando",
  "status.awaiting_release": "Aguardando merge / release",
  "status.done": "Concluído",
  "status.blocked": "Bloqueado",
  "status.waiting_resource": "Aguardando recurso",
  "status.paused": "Pausado",
  "status.failed": "Falhou",
  "status.cancelled": "Cancelado",
  "status.awaiting_human": "Aguardando decisão humana",
  "step.waiting": "Aguardando",
  "step.in_progress": "Em andamento",
  "step.done": "Concluído",
  "step.failed": "Falhou",
  "step.blocked": "Bloqueado",
  "step.paused": "Pausado",
  "step.cancelled": "Cancelado",

  "settings.title": "Configurações",
  "settings.tab.infrastructure": "Infraestrutura e executores",
  "settings.tab.providers": "Provedores e chaves",
  "settings.tab.models": "Modelos e combos",
  "settings.tab.agents": "Agentes e modelos",
  "settings.tab.quotas": "Cotas e orçamento",
  "settings.tab.context": "Contexto e memória",
  "settings.tab.history": "Histórico",
  "settings.tab.permissions": "Permissões",
  "project.start": "Iniciar",
  "project.execution.startRequested": "Início solicitado.",
  "project.execution.pauseRequested": "Pausa solicitada.",
  "project.execution.resumeRequested": "Retomada solicitada.",
  "project.executionRefused": "O servidor não iniciou a execução",
  "project.openReadiness": "Abrir verificação de prontidão",
  "project.versionConflict": "O projeto mudou nesse meio-tempo. Os dados mais recentes foram recarregados; tente de novo.",
  "settings.tab.gauntlet": "Gauntlet Loop",
  "overview.fullLogs": "Ver logs completos",
  "overview.attemptHistory": "Histórico de tentativas",
  "runs.subtitle": "Tentativas de execução dos projetos.",
  "observability.subtitle": "Saúde operacional, logs, rastreamento de processos e alertas.",
  "agents.heading": "Configure agentes e modelos em Configurações",
  "agents.body": "Perfis de agente por papel, modelos e pools de cota são configurados em Configurações. A qualidade medida por modelo chega em uma fase posterior.",
  ...ptBRWave2,
  ...ptBRWave3,
  ...ptBRModules,
  ...ptBRAgents,
  ...ptBRWave5,
  ...ptBRRouting,
  ...ptBRAssistant,
  ...ptBROnboarding,
  ...ptBRMicrosystem,
  ...ptBRGuardian,
  ...ptBRBlocks,
  ...ptBRSecurity,
  ...ptBRComposer,
  ...ptBRShell,
  ...ptBRContent,
  ...ptBRDependencies,
  ...ptBROperations,
  ...ptBRGrant,
  ...ptBRSkills,
  "memory.subtitle": "Memória do projeto, fontes e avaliações.",
  "inbox.subtitle": "Entregas externas (GitHub, Sentry) e seu processamento, inclusive as sem projeto confirmado.",
  "integrations.subtitle": "Conexões GitHub App e Sentry, incidentes e pull requests.",
  "overview.previewUnavailableTitle": "Nenhuma prévia disponível",
  "overview.previewUnavailableBody": "Uma prévia mostra um alvo registrado deste projeto, com versão e atualidade. Registre um em Prévia e testes; nenhuma imagem da aplicação é exibida até existir uma captura real.",
  "projectSettings.lastChecked": "Verificado {when}",
  "projectSettings.neverChecked": "Nunca verificado",
};
